/**
 * OTP delivery eligibility — the hard gate before Verifik ever sees a request.
 *
 * WHY THIS EXISTS. A verification message costs a credit whether or not it can
 * ever arrive. E2E used to call the OTP endpoint with synthetic numbers, and a
 * provider bills the API call even when the destination is unassignable — which
 * is also the exact signature of SMS pumping, and pumping is what gets an
 * account suspended. Real people must keep receiving codes; tests, landlines
 * and numbers that cannot exist must never reach the provider.
 *
 * WHAT CHANGED. This file used to hold the rule itself: a `+503` prefix test
 * for 6/7, hand-written, correct for one country out of ten. The rule now comes
 * from `libphonenumber-js/max`, which carries every country's real numbering
 * plan and the line type, so a Guatemalan landline and a Belizean number that
 * cannot exist are refused on the same evidence as a Salvadoran one. This file
 * is the name callers use for the decision, not the decision itself.
 *
 * This is a mathematical barrier, not a hint. Every caller MUST short-circuit
 * when it returns false: no send request, no link request, no navigation to the
 * code screen.
 */
import {
  isOtpCapablePhone,
  isPlausiblePhoneNumber,
  isSupportedPhone,
} from '../validations/auth.schema'

/**
 * True only when this E.164 is a number that can actually receive a WhatsApp
 * OTP in one of our supported markets: it exists under its country's numbering
 * plan, that country is one we verify, and the line is one a handset answers.
 */
export function canDeliverOtp(phone: string): boolean {
  return isOtpCapablePhone(phone.trim())
}

/** Stable machine code for undeliverable / test-only numbers. */
export const OTP_UNDELIVERABLE_CODE = 'PHONE_UNDELIVERABLE' as const

/**
 * Why a number cannot be sent to — so a disabled button can say so.
 *
 * A gate that silently refuses is half a feature. The three reasons need three
 * different sentences because they need three different actions from the
 * person: keep typing, fix a digit, or use a different handset.
 */
export type OtpIneligibility =
  /** Nothing typed yet. Say nothing — this is not an error, it is a blank field. */
  | 'empty'
  /** Not a number that exists under that country's plan. Usually still typing. */
  | 'not_a_number'
  /** A real number, in a country we do not verify. */
  | 'unsupported_country'
  /** A real number here, on a line no handset answers — a landline. */
  | 'not_mobile'

/**
 * Classify a prefix+digits candidate. Returns null when it IS sendable.
 *
 * Ordering matters: 'not_a_number' has to be tested before the country and the
 * line type, because neither of those questions has an answer for a string that
 * is not yet a number, and reporting "we do not serve that country" to someone
 * three digits into their own is worse than saying nothing.
 */
export function classifyOtpIneligibility(candidate: string): OtpIneligibility | null {
  const normalized = candidate.trim()
  if (!normalized || normalized === '+' || !/\d/.test(normalized)) return 'empty'
  if (!isPlausiblePhoneNumber(normalized)) return 'not_a_number'
  if (!isSupportedPhone(normalized)) return 'unsupported_country'
  if (!isOtpCapablePhone(normalized)) return 'not_mobile'
  return null
}

/**
 * The i18n key that explains an ineligibility to the person who caused it.
 *
 * Lives next to the classifier rather than in each screen so the phone-entry
 * screen and the phone-linking screen cannot drift into saying different things
 * about the same number.
 */
export function otpIneligibilityMessageKey(reason: OtpIneligibility): string | null {
  switch (reason) {
    // A blank field is not a mistake. Saying anything here turns "start typing"
    // into "you did something wrong".
    case 'empty':
      return null
    case 'not_a_number':
      return 'auth.validation.phone_format'
    case 'unsupported_country':
      return 'auth.validation.phone_unsupported_country'
    case 'not_mobile':
      return 'auth.validation.phone_not_mobile'
  }
}
